import { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LANGUAGES } from "@/i18n";
import { Check, Globe, Palette, Sun, Moon, Monitor, KeyRound } from "lucide-react";

const THEME_OPTIONS = [
  { code: "light", icon: Sun },
  { code: "dark", icon: Moon },
  { code: "system", icon: Monitor },
];

export default function Preferences() {
  const { lang, setLang, t } = useLang();
  const { theme, setTheme } = useTheme();
  const { user, demoMode } = useAuth();

  const chooseLang = (code) => { setLang(code); toast.success(t("prefs.saved")); };
  const chooseTheme = (code) => { setTheme(code); toast.success(t("prefs.saved")); };

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: pw.current, newPassword: pw.next });
      toast.success("Password changed.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title={t("prefs.title")} subtitle={t("prefs.subtitle")} />
      <div className="max-w-2xl space-y-8 p-6 md:p-10">
        <div className="flex items-center gap-4 border border-zinc-200 bg-white p-5">
          <div className="flex h-12 w-12 items-center justify-center bg-zinc-900 text-sm font-bold text-white">
            {(user?.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-bold text-zinc-900">{user?.name}</div>
            <div className="font-mono text-xs text-zinc-400">{user?.email} · {user?.role}</div>
          </div>
        </div>

        <section className="border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
            <Palette size={15} /> {t("prefs.theme")}
          </div>
          <div className="p-5">
            <p className="mb-4 text-sm text-zinc-500">{t("prefs.themeHelp")}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {THEME_OPTIONS.map((o) => {
                const on = theme === o.code;
                return (
                  <button
                    key={o.code}
                    onClick={() => chooseTheme(o.code)}
                    data-testid={`theme-option-${o.code}`}
                    className={`flex items-center justify-between border px-4 py-3 text-left transition-colors ${
                      on ? "border-primary bg-primary/5" : "border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <o.icon size={18} className={on ? "text-primary" : "text-zinc-500"} />
                      <span className="text-sm font-semibold text-zinc-800">{t(`prefs.theme_${o.code}`)}</span>
                    </span>
                    {on && <Check size={18} className="text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
            <Globe size={15} /> {t("prefs.language")}
          </div>
          <div className="p-5">
            <p className="mb-4 text-sm text-zinc-500">{t("prefs.languageHelp")}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {LANGUAGES.map((l) => {
                const on = lang === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => chooseLang(l.code)}
                    data-testid={`lang-option-${l.code}`}
                    className={`flex items-center justify-between border px-4 py-3 text-left transition-colors ${
                      on ? "border-primary bg-primary/5" : "border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-xl">{l.flag}</span>
                      <span className="text-sm font-semibold text-zinc-800">{l.label}</span>
                    </span>
                    {on && <Check size={18} className="text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {!user?.external && !demoMode && (
          <section className="border border-zinc-200 bg-white" data-testid="change-password-section">
            <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
              <KeyRound size={15} /> Password
            </div>
            <form onSubmit={changePassword} className="space-y-4 p-5">
              <p className="text-sm text-zinc-500">Change the password you use to sign in to this console.</p>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Current password</Label>
                  <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
                    className="rounded-sm border-zinc-300" data-testid="cp-current" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">New password</Label>
                  <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                    className="rounded-sm border-zinc-300" data-testid="cp-new" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Confirm new</Label>
                  <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                    className="rounded-sm border-zinc-300" data-testid="cp-confirm" required />
                </div>
              </div>
              <div>
                <Button type="submit" disabled={saving} data-testid="cp-submit"
                  className="rounded-sm bg-primary hover:bg-[#002480]">
                  {saving ? "Saving…" : "Update password"}
                </Button>
              </div>
            </form>
          </section>
        )}
      </div>
    </>
  );
}
